import classNames from 'classnames';
import {
    AnimatePresence,
    Target,
    TargetAndTransition,
    motion,
} from 'framer-motion';
import { ReactNode, useEffect, useRef } from 'react';

import PaddedBox from '../../../../../components/PaddedBox/PaddedBox';
import useIsMobile from '../../../../../hooks/ui/useIsMobile';
import useMeasure from '../../../../../hooks/useMeasure';
import borderRadiusAsClasses from '../../../../../shared/borderRadiusAsClasses';
import TopBottomBorderRadius from '../../../../../types/UI/TopBottomBorderRadius';
import ComposeBookingStep from '../../../../types/ComposeBookingStep';

import determineDirection from './determineDirection';

import styles from './StepContainer.module.css';

const stepOrder: Array<ComposeBookingStep> = [
    'dock',
    'boatModel',
    'numberOfBoats',
    'passengers',
    'pickupDate',
];

const initialStyle = (width: number, direction: 1 | 0 | -1 = 0): Target => ({
    x: direction * width,
    opacity: 0,
});

const exitStyle = (
    width: number,
    direction: 1 | 0 | -1 = 0
): TargetAndTransition => ({
    position: 'absolute',
    opacity: 0,
    top: 0,
    left: 0,
    right: 0,
    x: -1 * direction * width,
});

type StepContainerProps = {
    children: ReactNode;
    stepName: ComposeBookingStep;
    borderRadius?: TopBottomBorderRadius;
};

const StepContainer = ({
    children,
    stepName,
    borderRadius = 'none',
}: StepContainerProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const lastStepName = useRef(stepName);
    const measure = useMeasure(containerRef);

    const direction = determineDirection(
        stepOrder,
        stepName,
        lastStepName.current
    );

    useEffect(() => {
        lastStepName.current = stepName;
    }, [stepName]);

    const isMobile = useIsMobile();

    return (
        <div className={styles.Root} ref={containerRef}>
            <PaddedBox
                variant="white"
                borderRadius={borderRadius}
                className={classNames(
                    styles.Background,
                    borderRadiusAsClasses(borderRadius, {
                        before: true,
                        transition: true,
                    })
                )}
                verticalPadding="none"
            >
                <motion.div
                    initial={false}
                    animate={{
                        height: measure.height > 0 ? measure.height : '80vh',
                    }}
                    transition={{ duration: 0.25, ease: 'circOut' }}
                />
            </PaddedBox>

            <AnimatePresence>
                <motion.div
                    key={stepName}
                    initial={initialStyle(
                        measure.width,
                        isMobile ? direction : 0
                    )}
                    animate={{
                        x: 0,
                        opacity: 1,
                        transition: {
                            delay: isMobile ? 0 : 0.15,
                            duration: 0.25,
                            ease: 'circOut',
                        },
                    }}
                    exit={exitStyle(measure.width, isMobile ? direction : 0)}
                    transition={{
                        duration: 0.25,
                        ease: 'circOut',
                    }}
                >
                    {children}
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

export default StepContainer;
